import {
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Button,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import EngineeringIcon from "@mui/icons-material/Engineering";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import dangerIcon from "../Img/danger.png";

export default function SiteWorkersSidebar({
  open,
  onClose,
  siteName,
  siteAddress,
  workers,
  onOpenHistory,
}) {

  const { t } = useTranslation();

  if (!open) return null;

  const statusChip = (w) => {
    const base = {
      height: 26,
      fontSize: 12,
      fontWeight: 800,
      borderRadius: 2,
      px: 1.2,
    };

    if (w.presence === "Working") {
      return (
        <Chip
          label={t("On Site")}
          size="small"
          sx={{
            ...base,
            bgcolor: "#ecfdf3",
            color: "#166534",
            border: "1px solid #bbf7d0",
          }}
        />
      );
    }

    if (w.presence === "OFF") {
      return (
        <Chip
          label={t("Checked Out")}
          size="small"
          sx={{
            ...base,
            bgcolor: "#f3f4f6",
            color: "#111827",
            border: "1px solid #e5e7eb",
          }}
        />
      );
    }

    return (
      <Chip
        label={t("Absent")}
        size="small"
        sx={{
          ...base,
          bgcolor: "#fff7ed",
          color: "#9a3412",
          border: "1px solid #fed7aa",
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 380 },
        height: "100%",
        bgcolor: "#fff",
        borderRight: { xs: "none", md: "1px solid #e5e7eb" },
        boxShadow: { xs: "none", md: "0 10px 30px rgba(0,0,0,0.08)" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2.2, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#111827" }}>
            {siteName || t("Construction Site")}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.4 }}>
            {siteAddress || t("No address provided")}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.6 }}>
            {workers?.length ?? 0} {t("worker(s)")}
          </Typography>
        </Box>

        <IconButton onClick={onClose} sx={{ mt: -0.2 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* List */}
      <Box sx={{ p: 1, overflowY: "auto", flex: 1 }}>
        {(!workers || workers.length === 0) ? (
          <Typography sx={{ color: "#6b7280", fontSize: 13, mt: 2 }}>
            {t("No workers assigned to this site.")}
          </Typography>
        ) : (
          workers.map((w) => (
            <Box
              key={w.id}
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 2.5,
                mb: 1.4,
                bgcolor: "#fff",
                boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
                overflow: "hidden",
              }}
            >
              {/* Ticket header: ID (left) + status (right) */}
              <Box
                sx={{
                  px: 1.6,
                  pt: 1.4,
                  pb: 1.1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                }}
              >
                {/* Left side takes remaining width and is allowed to shrink */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,     // IMPORTANT: enables ellipsis inside flex
                    flex: 1,         // take remaining space
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#fff1f2",
                      color: "#c93d3d",
                      border: "1px solid #ffe4e6",
                      flexShrink: 0, // icon never shrinks
                    }}
                  >
                    <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>

                  <Tooltip title={`#${w.id}`} placement="top" arrow>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: 14,
                        color: "#111827",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      #{w.id}
                    </Typography>
                  </Tooltip>
                </Box>

                {/* Right side fixed; never pushed out */}
                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                  {statusChip(w)}
                </Box>
              </Box>

              {/* Progress vibe line (like the screenshot) */}
              <Box sx={{ px: 1.6, pb: 1.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      bgcolor: w.presence === "Working" ? "#16a34a" : "#9ca3af",
                      boxShadow: "0 0 0 4px rgba(22,163,74,0.12)",
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      height: 4,
                      borderRadius: 999,
                      bgcolor: "#e5e7eb",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width:
                          w.presence === "Working"
                            ? "70%"
                            : w.presence === "OFF"
                            ? "100%"
                            : "25%",
                        bgcolor: w.presence === "Absent" ? "#9ca3af" : "#16a34a",
                        borderRadius: 999,
                      }}
                    />
                  </Box>

                  <EngineeringIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                </Box>
              </Box>

              {/* Body */}
              <Box sx={{ px: 1.6, pb: 1.4 }}>
                {/* Worker row: avatar + name + history btn */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: "#f3f4f6",
                      color: "#111827",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <EngineeringIcon sx={{ fontSize: 18 }} />
                  </Avatar>

                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 14, color: "#111827" }}>
                      {w.fullName || t("Worker")}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.2 }}>
                      {t("Construction Worker")}
                    </Typography>
                  </Box>
                </Box>

                {/* Times row */}
                <Box sx={{ display: "flex", gap: 1, mt: 1.2, flexWrap: "wrap" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.7,
                      px: 1,
                      py: 0.65,
                      borderRadius: 2,
                      bgcolor: "#ecfdf3",
                      color: "#166534",
                      fontSize: 12,
                      fontWeight: 800,
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <EventAvailableIcon sx={{ fontSize: 16 }} />
                    <span>{t("In")}</span>
                    <span style={{ fontWeight: 900 }}>{w.clockInTime || "—"}</span>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.7,
                      px: 1,
                      py: 0.65,
                      borderRadius: 2,
                      bgcolor: "#fef2f2",
                      color: "#991b1b",
                      fontSize: 12,
                      fontWeight: 800,
                      border: "1px solid #fecaca",
                    }}
                  >
                    <EventBusyIcon sx={{ fontSize: 16 }} />
                    <span>{t("Out")}</span>
                    <span style={{ fontWeight: 900 }}>{w.clockOutTime || "—"}</span>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.7,
                      px: 1,
                      py: 0.65,
                      borderRadius: 2,
                      bgcolor: "#f3f4f6",
                      color: "#111827",
                      fontSize: 12,
                      fontWeight: 800,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <QueryBuilderIcon sx={{ fontSize: 16 }} />
                    <span>{w.hoursWorked || "—"}</span>
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ManageSearchIcon />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      fontWeight: 800,
                      borderColor: "#e5e7eb",
                    }}
                    onClick={() => onOpenHistory?.(w)}
                  >
                    {t("History")}
                  </Button>
                </Box>

                {/* Divider + contact */}
                <Divider sx={{ my: 1.4 }} />

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        color: "#111827",
                      }}
                    >
                      <EmailOutlinedIcon sx={{ fontSize: 18, color: "#111827" }} />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 11.5, color: "#6b7280", fontWeight: 800 }}>
                        {t("Email")}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: "#111827", fontWeight: 800 }}>
                        {w.email || "—"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        color: "#111827",
                      }}
                    >
                      <PhoneOutlinedIcon sx={{ fontSize: 18, color: "#111827" }} />
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: 11.5, color: "#6b7280", fontWeight: 800 }}>
                        {t("Phone")}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: "#111827", fontWeight: 800 }}>
                        {w.phone || "—"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                  {/* Violations (shown only when worker has violations) */}
                  {Array.isArray(w.violations) && w.violations.length > 0 && (
                    <Box
                      sx={{
                        mt: 1.2,
                        p: 1.2,
                        borderRadius: 2,
                        border: "1px solid #fed7aa",
                        bgcolor: "#fff7ed",
                        display: "flex",
                        gap: 1.1,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 1.8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#ffedd5",
                          border: "1px solid #fed7aa",
                          color: "#9a3412",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={dangerIcon}
                          alt={t("Danger")}
                          style={{
                            width: 30,
                            height: 30,
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
 

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 0.8 }}>
                          {w.violations.map((v, i) => (
                            <Chip
                              key={`${w.id}-v-${i}`}
                             label={t(v)}
                              size="small"
                              sx={{
                                height: 26,
                                fontSize: 12,
                                fontWeight: 900,
                                borderRadius: 2,
                                bgcolor: "#ffffff",
                                border: "1px solid #fed7aa",
                                color: "#9a3412",
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  )}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
